import { DateFormatPattern, SynthesisGroup, Task } from '../api/types';
import { formatDate } from './dateFormat';

/**
 * Constantes et helpers partagés entre `CompiledSynthesisChart.tsx` (rendu
 * React à l'écran) et `compiledPdfExport.ts` (export PDF du même diagramme
 * compilé) — une seule source de vérité pour que l'export corresponde
 * exactement à ce qui est affiché (voir demande utilisateur d'homogénéiser
 * les deux). Distinct de `pdfLayout.ts` (qui sert l'export PAR diagramme
 * source, avec jusqu'à 9 colonnes et des connecteurs) : le diagramme
 * compilé a ses propres proportions (5 colonnes fixes, pas de connecteurs,
 * des bandeaux séparateurs de hauteur différente des lignes de tâche).
 */

export const DAY_WIDTH = 24;
export const ROW_HEIGHT = 28;
export const SEPARATOR_HEIGHT = 26;
export const MONTH_ROW_HEIGHT = 18;
export const WEEK_ROW_HEIGHT = 16;
export const DAY_ROW_HEIGHT = 18;
export const HEADER_HEIGHT = MONTH_ROW_HEIGHT + WEEK_ROW_HEIGHT + DAY_ROW_HEIGHT;
export const BAR_HEIGHT = 16;
export const NUM_COL_WIDTH = 30;
export const COLUMN_WIDTH: Record<string, number> = { nameFr: 170, nameEn: 150, duration: 55, start: 78, end: 78 };
export const COMPILED_COLUMNS = ['nameFr', 'nameEn', 'duration', 'start', 'end'] as const;
export const LABEL_WIDTH = NUM_COL_WIDTH + COMPILED_COLUMNS.reduce((sum, c) => sum + COLUMN_WIDTH[c], 0);
export const INDENT_PX = 12;

export type CompiledRow =
  | { kind: 'separator'; key: string; diagramId: string; diagramName: string; assigneeName: string | null }
  | { kind: 'task'; key: string; task: Task };

/** Aplatit les groupes (un par diagramme source) en une seule liste ordonnée : un bandeau séparateur suivi des tâches de ce groupe, pour chaque groupe. */
export function buildCompiledRows(groups: SynthesisGroup[]): CompiledRow[] {
  const out: CompiledRow[] = [];
  for (const g of groups) {
    out.push({
      kind: 'separator',
      key: `sep-${g.diagramId}`,
      diagramId: g.diagramId,
      diagramName: g.diagramName,
      assigneeName: g.assignee ? `${g.assignee.firstName} ${g.assignee.lastName}` : null,
    });
    for (const task of g.tasks) out.push({ kind: 'task', key: task.id, task });
  }
  return out;
}

export interface CompiledRowLayout {
  row: CompiledRow;
  top: number;
  height: number;
}

/** Position verticale cumulée de chaque ligne (bandeau ou tâche), hauteurs différentes selon le type — sert à la fois au rendu écran et au découpage en pages de l'export PDF. */
export function computeCompiledRowLayout(rows: CompiledRow[]): CompiledRowLayout[] {
  let y = 0;
  return rows.map((row) => {
    const height = row.kind === 'separator' ? SEPARATOR_HEIGHT : ROW_HEIGHT;
    const entry = { row, top: y, height };
    y += height;
    return entry;
  });
}

export function compiledColumnText(task: Task, col: (typeof COMPILED_COLUMNS)[number], dateFormat: DateFormatPattern): string {
  switch (col) {
    case 'nameFr':
      return task.nameFr;
    case 'nameEn':
      return task.nameEn;
    case 'duration':
      return task.durationDays != null ? String(task.durationDays) : '';
    case 'start':
      return formatDate(task.startDate, dateFormat);
    case 'end':
      return formatDate(task.endDate, dateFormat);
  }
}
